// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartPropsContainer.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import { observer } from 'mobx-react';
import connect from 'share/enhancers/Izenda.Connect';
import { find, isNil, filter, get } from 'lodash/fp';

import ReportPartTypeManager from 'Izenda.ReportPartTypeManager';
import Panel from 'share/components/controls/Izenda.Panel';
import FormGroup from 'share/components/controls/Izenda.FormGroup';
import DropDown from 'share/components/controls/Izenda.DropDown';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import PropertiesEditorContainer from 'share/components/controls/Izenda.PropertiesEditorContainer';
import { REPORT_PART_TYPE, REPORT_PART_PERMISSIONS } from 'logic/data/Izenda.ReportDesignerConstants';
import ToggleViewModePropertyEditor from 'share/components/controls/Izenda.ToggleViewModePropertyEditor';
import ReportPartCommonSectionPropsSchema from 'logic/data/Izenda.ReportPartCommonSectionPropsSchema';

/**
 * Properties editor container of Report Part.
 * This component displays the report part's properties in Properties editor panel of Report Designer
 */
@connect()
export default class ReportPartPropsContainer extends Component {
  static propTypes = {
    reportDesignerModel: PropTypes.object.isRequired
  };

  render() {
    const { reportDesignerModel, localization } = this.props;
    const options = this.getAccessibleReportParts(reportDesignerModel.supportReportParts);
    const configReportPartId = get('configReportPart.id', reportDesignerModel);
    const configReportPartType = get('configReportPart.type', reportDesignerModel);

    const unknowType = isNil(configReportPartId) || isNil(configReportPartType);

    //TODO: consider to move this logic out to Form schema
    const showViewMode = configReportPartType !== REPORT_PART_TYPE.Form && !unknowType;

    return (
      <section className="form-horizontal field-props-container">
        <div className="field-props-header">
          <div className="title-group ">
            <strong className="u_inlineBlock">
              {localization.getText('REPORT_PART_PROPERTIES_CONTAINER_REPORT_PART_PROPERTIES')}
            </strong>
            {showViewMode && <ToggleViewModePropertyEditor />}
          </div>
          <FormGroup className="report-parts">
            <DropDown
              blankOption
              options={options}
              onChange={this.onChangeReportPart}
              valueField="id"
              labelField="headerTitle"
              value={unknowType ? null : configReportPartId}
            />
          </FormGroup>
        </div>

        <fieldset className="field-props-content" disabled={!configReportPartId}>
          <ScrollBox>
            {unknowType ? this.renderCommonSectionProps() : this.renderPropertiesEditorByReportType()}
          </ScrollBox>
        </fieldset>
      </section>
    );
  }

  onChangeReportPart = (selectedPartTitle, selectedPart) => {
    const reportBody = get('reportDesignerModel.reportFields.reportBody', this.props);
    ensure(!!reportBody, 'Cannot find report body inside report designer model bound to the properties editor');

    reportBody.setConfigReportPart(selectedPart);
  };

  renderPropertiesEditorByReportType() {
    const props = this.props;
    const configReportPart = props.reportDesignerModel.configReportPart;
    const reportPartType = configReportPart && ReportPartTypeManager.getReportPartType(configReportPart.type);
    const reportPartPropertiesConfig = reportPartType && get('properties.reportPart', reportPartType);
    let propConfig;
    const localization = props.localization;

    if (reportPartPropertiesConfig) {
      propConfig =
        reportPartPropertiesConfig &&
        reportPartPropertiesConfig(props.reportDesignerModel, configReportPart, props.fieldStore, props);
      return <PropertiesEditorContainer propertyConfig={propConfig} />;
    }

    return (
      reportPartType &&
      reportPartType.label && (
        <strong>{`${localization.getText('REPORTDESIGNER_MESSAGE_DO_NOT_SUPPORT')} [${reportPartType.label}]`}</strong>
      )
    );
  }

  renderCommonSectionProps() {
    const propConfig = {
      definedSchema: ReportPartCommonSectionPropsSchema,
      source: null,
      widgets: {
        Panel
      },
      onChangeValue() {}
    };

    return <PropertiesEditorContainer propertyConfig={propConfig} />;
  }

  /**
   * Get the accessible report parts base on current user permissions
   * @param {Array<Object>} reportParts Available report parts
   */
  getAccessibleReportParts(reportParts) {
    const authorization = this.props.authorization;

    const checkPrivileges = reportPartType => {
      const reportPartConfig = find(item => item.value === reportPartType, REPORT_PART_PERMISSIONS);

      if (!isNil(reportPartConfig) && reportPartConfig.permissions) {
        return authorization.checkPrivileges(reportPartConfig.permissions);
      }
      return true;
    };

    return filter(item => checkPrivileges(item.type), reportParts);
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.ReportPartPropsContainer.js